import React, { useCallback, useEffect, useState } from "react";
import "./Groups.scss";
import DataTableSubHeader from "../../Components/DataTableSubHeader/DataTableSubHeader.jsx";
import ItemsTable from "../../Components/ItemsTable/ItemsTable.jsx";
import { AddIcon, FilterIcon } from "../../assets/icons/index.js";
import { toast } from "react-toastify";
import { getGroups, getOrganizations, deleteGroup } from "../../api/index.js";
import useTransformData from "../../hooks/useTransformData.js";
import AddGroupModal from "../../Components/GroupsDetailsTable/AddGroupModal/AddGroupModal.jsx";
import DeleteModal from "../../Components/Common/DeleteModal/DeleteModal.jsx";
import FilterModal from "../../Components/FilterModal/index.jsx";
import ReactModal from "../../Components/ReactModal/ReactModal.jsx";
import { useAppContext } from "../../Components/Context/AppContext.jsx";
import userHasPermission from "../../helpers/formTools/userHasPermission/index.js";
import AdminDropdown from "../../Components/AdminDropdown/AdminDropdown.jsx";
import Spinner from "../../Components/ProgressBar/Spinner.js";

const selectedHeaders = [
  { key: "name", label: "Nome gruppo", width: "300px", sortable: false },
  { key: "organizzazione", label: "Organizzazione", width: "300px", sortable: false },
  { key: "color", label: "Colore", width: "300px", sortable: false },
  { key: "actions", label: "", width: "300px", sortable: false },
];

const breadcrumbsData = [
  {
    id: 1,
    label: `Amministrazione ${localStorage.getItem("username")}`,
    iconUrl: true,
  },
  {
    id: 2,
    label: "GRUPPI",
    iconUrl: "",
  },
];

const filterValues = [
  { label: "Nome", key: "name" },
  { label: "Organizzazione", key: "organization" },
];

const config = {
  fields: [
    { name: "name", type: "text" },
    { name: "organizzazione", type: "text" },
    { name: "color", type: "color" },
  ],
};

const GroupsDetailsTable = () => {
  const [tableData, setTableData] = useState([]);
  const [isMenuActive, setMenuActive] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState({});
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [organizationsData, setOrganizationsData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const { userProfile, groupFilter, setGroupFilter } = useAppContext();
  const organization = JSON.parse(localStorage.getItem("organization"));
  const userCanRead = userHasPermission({
    userProfile,
    organization,
    resource: "GROUPS",
    permission: "READ",
  });
  const userCanWrite = userHasPermission({
    userProfile,
    organization,
    resource: "GROUPS",
    permission: "WRITE",
  });
  const userCanDelete = userHasPermission({
    userProfile,
    organization,
    resource: "GROUPS",
    permission: "DELETE",
  });

  const handleViewGroup = useCallback((item) => {
    setSelectedGroup(item);
    setIsAddModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((item) => {
    setSelectedGroup(item);
    setIsDeleteModalOpen(true);
  }, []);

  const transformedData = useTransformData(
    tableData,
    handleViewGroup,
    handleDeleteClick,
    config,
    userCanDelete
  );

  const subHeaderRightNavOptions = {
    icons: [
      userCanWrite && {
        icon: <AddIcon fillColor="var(--archeo-color)" height="28" width="28" />,
        onClick: () => setIsAddModalOpen(true),
        label: "Aggiungi",
      },
      {
        icon: <FilterIcon fillColor="var(--archeo-color)" height="28" width="28" />,
        onClick: () => setIsFilterModalOpen(true),
        label: "Filtro",
      },
    ].filter(Boolean),
    components: [
      <AdminDropdown defaultSelected="GRUPPI"/>,
    ],
  };

  const fetchGroups = useCallback(async () => {
    try {
      const filter = {
        page: currentPage,
        pageSize: recordsPerPage,
      };

      groupFilter.forEach((f) => {
        if (f.value) {
          if (f.key === "organization") {
            const search = f.value.trim().toLowerCase();
            const org = organizationsData.find(
              (o) => o.label.toLowerCase().includes(search)
            );
            if (org) {
              filter["organization.id"] = org.value;
            } else {
              filter["organization.id"] = "__no_match__";
            }
          } else {
            filter[f.key] = f.value;
          }
        }
      });

      const response = await getGroups(filter);
      setTableData(response.results?.map((group) => ({
        ...group,
        organizzazione: group.organization?.name,
      })));
      setTotalRecords(response.totalElements);
      setIsLoading(false);
    } catch (error) {
      toast.error("Error fetching groups");
    }
  }, [currentPage, recordsPerPage, groupFilter, organizationsData]);

  const fetchOrganizations = useCallback(async () => {
    try {
      const organizations = await getOrganizations({ pageSize: 100, page: 1 });
      if (organizations) {
        setOrganizationsData(
          organizations.map((org) => ({ value: org.id, label: org.name }))
        );
      }
    } catch (err) {
      console.error("Error while fetching organizations:", err);
    }
  }, []);

  const handleCallBackFilter = useCallback(
    (data) => {
      const filter = data.map((item) => ({
        key: filterValues?.find((f) => f.label === item.label)?.key,
        value: item.value,
      }));
      setGroupFilter(filter);
      setCurrentPage(1);
      return true;
    },
    [setGroupFilter]
  );

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const handleRecordsPerPageChange = useCallback((newRecordsPerPage) => {
    setRecordsPerPage(newRecordsPerPage);
    setCurrentPage(1);
  }, []);

  const handleDelete = useCallback(async () => {
    try {
      await deleteGroup(selectedGroup.id);
      await fetchGroups();
      toast.success("Group deleted successfully");
    } catch (err) {
      console.error("Error while deleting group", err);
      toast.error(
        "Prima di cancellare un gruppo, controlla che questo non sia in uso nel sistema associato a utenti o risorse"
      );
    } finally {
      setIsDeleteModalOpen(false);
    }
  }, [selectedGroup, fetchGroups]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  return (
    <div className="container">
      <main className="page-main-content">
      <DataTableSubHeader
        title={"GRUPPI"}
        breadcrumbsData={breadcrumbsData}
        subHeaderRightNavOptions={subHeaderRightNavOptions}
        isMenuActive={isMenuActive}
        setMenuActive={() => setMenuActive(!isMenuActive)}
      />
      {isLoading ? (
            <Spinner />
          ) : (
         <ItemsTable
            data={transformedData}
            headers={selectedHeaders}
            filterValues={filterValues}
            totalRecords={totalRecords}
            currentPage={currentPage}
            recordsPerPage={recordsPerPage}
            onPageChange={handlePageChange}
            onRecordsPerPageChange={handleRecordsPerPageChange}
            userCanRead={userCanRead}
        />)
      }
      </main>
      {isAddModalOpen && (
        <AddGroupModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          fetchGroups={fetchGroups}
          organization={organization}
          organizationsData={organizationsData}
        />
      )}
      
      {isDeleteModalOpen && (
        <DeleteModal
          show={isDeleteModalOpen}
          item={selectedGroup}
          handleClose={() => setIsDeleteModalOpen(false)}
          handleDelete={handleDelete}
          itemType="Group"
        />
      )}
      <ReactModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
      >
        <FilterModal
          closeModal={() => setIsFilterModalOpen(false)}
          filterValues={filterValues}
          callBackFilter={handleCallBackFilter}
          onReset={() => {
            setGroupFilter([]);
            setCurrentPage(1);
            window.location.reload();
          }}
        />
      </ReactModal>
    </div>
  );
};

export default GroupsDetailsTable;
